import { useEffect } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import confetti from "canvas-confetti";
import { usePostHog } from "posthog-js/react";
import Image from "@/components/image";
import { useActivePhase } from "../useActivePhase";
import { animationFor } from "../sectionAnimation";

const parseSurveyUrl = (
  raw: string | Record<string, unknown> | undefined,
): string | undefined => {
  if (!raw) return undefined;
  if (typeof raw === "string") {
    try {
      return (JSON.parse(raw) as { surveyUrl?: string }).surveyUrl;
    } catch {
      return undefined;
    }
  }
  return (raw as { surveyUrl?: string }).surveyUrl;
};

type CertificateSectionProps = {
  award: {
    id: string;
    title: string;
    awardType: string;
    image?: { url: string; alt: string };
    certificateConfig?: { metadata?: string | Record<string, unknown> };
  };
  isActive: boolean;
  onAdvance: () => void;
  isLast: boolean;
};

export const CertificateSection = ({
  award,
  isActive,
  onAdvance,
  isLast,
}: CertificateSectionProps) => {
  const posthog = usePostHog();
  const phase = useActivePhase(isActive);

  useEffect(() => {
    if (!isActive) return undefined;
    posthog?.capture("certificate-completion", {
      award_id: award.id,
      award_title: award.title,
      award_type: award.awardType,
    });

    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    document.body.appendChild(canvas);
    const myConfetti = confetti.create(canvas, { resize: true });

    myConfetti({
      particleCount: 120,
      spread: 90,
      origin: { x: 0.5, y: 0.4 },
      disableForReducedMotion: true,
    });
    const t = setTimeout(() => {
      myConfetti({
        particleCount: 60,
        spread: 70,
        origin: { x: 0.5, y: 0.4 },
        disableForReducedMotion: true,
      });
    }, 400);
    return () => {
      clearTimeout(t);
      myConfetti.reset();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [isActive, award.id, award.title, award.awardType, posthog]);

  const surveyUrl = parseSurveyUrl(award.certificateConfig?.metadata);

  const handleSurveyClick = () => {
    if (!surveyUrl) return;
    // Open the survey in a new tab so the celebration shade stays alive
    // and we can run the onAdvance acknowledge in this tab — otherwise
    // navigating away strands the celebration unacknowledged and it
    // re-fires next session.
    window.open(surveyUrl, "_blank", "noopener,noreferrer");
    onAdvance();
  };

  return (
    <Stack
      sx={{
        margin: "25px",
        padding: "24px 12px",
        backgroundColor: "#fff",
        width: "95%",
        maxWidth: "600px",
        borderRadius: 2,
        animation: animationFor(phase),
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        sx={{ padding: "12px 24px" }}
      >
        <Typography
          variant="h2"
          sx={{ fontFamily: "Nunito", fontWeight: 600, pb: 3 }}
        >
          Congratulations!
        </Typography>

        <Typography variant="h4" sx={{ mb: 4, textAlign: "center" }}>
          You&apos;ve just earned a new certificate for
          <br />
          {award.title}!
        </Typography>

        {award.image && (
          <Image
            alt={award.image.alt}
            src={award.image.url}
            sx={{ w: "240px", height: "240px", mb: 3 }}
          />
        )}
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            textAlign: "center",
            color: "text.primary",
            mt: "0 !important",
          }}
        >
          Download your certificate in Achievements.
        </Typography>

        {surveyUrl ? (
          <Stack
            sx={{
              width: "420px",
              maxWidth: "100%",
              p: 3,
              backgroundColor: "rgba(7, 141, 238, 0.08)",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontFamily: "ProximaNova Bold", pb: 1 }}
            >
              Please take a few minutes to tell us what you think.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ alignSelf: "center", width: "234px" }}
              onClick={handleSurveyClick}
            >
              Take Survey
            </Button>
          </Stack>
        ) : null}

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onAdvance}
            sx={{ width: "234px" }}
          >
            {isLast ? "Close" : "Next"}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};
